import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT } from "@/data/content";

const TYPE_LABELS = {
  admission: "admission",
  physiotherapy: "physiotherapy / rehab",
  general: "general",
};

const EnquiryForm = ({ defaultType = "admission", testId = "enquiry-form" }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    child_age: "",
    enquiry_type: defaultType,
    message: "",
  });

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone number are required.");
      return;
    }

    const typeLabel = TYPE_LABELS[form.enquiry_type] || "admission";
    const lines = [
      `Hello, I would like to enquire about ${typeLabel}.`,
      "",
      `Name: ${form.name.trim()}`,
      `Phone Number: ${form.phone.trim()}`,
    ];
    if (form.email.trim()) lines.push(`Email: ${form.email.trim()}`);
    if (form.child_age.trim()) lines.push(`Child's Age / Class: ${form.child_age.trim()}`);
    if (form.message.trim()) lines.push(`Message: ${form.message.trim()}`);

    const waUrl = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(
      lines.join("\n"),
    )}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp to send your enquiry…");
    setForm({
      name: "",
      phone: "",
      email: "",
      child_age: "",
      enquiry_type: defaultType,
      message: "",
    });
  };

  return (
    <form
      data-testid={testId}
      onSubmit={onSubmit}
      className="adhd-card p-7 sm:p-9 flex flex-col gap-5"
    >
      <div>
        <p className="adhd-eyebrow">{t("cta.enquire")}</p>
        <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-[color:var(--adhd-text)]">
          {t("form.title")}
        </h3>
        <p className="mt-1 text-sm text-[color:var(--adhd-muted)]">
          {t("form.subtitle")}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.name")}
          </Label>
          <Input
            data-testid="form-name"
            value={form.name}
            onChange={update("name")}
            required
            className="mt-1.5 rounded-xl"
            placeholder="Sangeeta Behera"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.phone")}
          </Label>
          <Input
            data-testid="form-phone"
            value={form.phone}
            onChange={update("phone")}
            required
            inputMode="tel"
            className="mt-1.5 rounded-xl"
            placeholder="94372 98628"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.email")}
          </Label>
          <Input
            data-testid="form-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            className="mt-1.5 rounded-xl"
            placeholder="parent@example.com"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.childAge")}
          </Label>
          <Input
            data-testid="form-childAge"
            value={form.child_age}
            onChange={update("child_age")}
            className="mt-1.5 rounded-xl"
            placeholder="e.g. 6 years"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.type")}
          </Label>
          <Select
            value={form.enquiry_type}
            onValueChange={(v) => setForm((p) => ({ ...p, enquiry_type: v }))}
          >
            <SelectTrigger
              data-testid="form-type"
              className="mt-1.5 rounded-xl"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admission">{t("form.typeAdmission")}</SelectItem>
              <SelectItem value="physiotherapy">{t("form.typePhysio")}</SelectItem>
              <SelectItem value="general">{t("form.typeGeneral")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
            {t("form.message")}
          </Label>
          <Textarea
            data-testid="form-message"
            rows={4}
            value={form.message}
            onChange={update("message")}
            className="mt-1.5 rounded-xl"
            placeholder="Tell us a little about your child or what you'd like to know..."
          />
        </div>
      </div>

      <button
        data-testid="form-submit"
        type="submit"
        className="adhd-btn-primary !w-full sm:!w-auto sm:!self-start"
      >
        <Send size={16} />
        {t("cta.submit")}
      </button>
    </form>
  );
};

export default EnquiryForm;
